import { i18n } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { PLAN_KEYS, PLANS } from '@magig/schemas';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnalyticsUsageCard } from '../components/analytics-usage-card.js';
import { Select } from '../components/select.js';
import { magToast } from '../components/toaster.js';
import { WorkspaceNav } from '../components/workspace-nav.js';
import { trackConversion } from '../lib/analytics.js';
import {
  addBillingTaxIdApi,
  type BillingAddress,
  type BillingPlanDisplay,
  type BillingState,
  type BillingTaxId,
  type BillingTopupPackDisplay,
  createCheckoutSessionApi,
  createPaymentMethodPortalApi,
  createTopupCheckoutSessionApi,
  type MagTokenTransactionRow,
  type PlanKey,
  removeBillingTaxIdApi,
  type SubscriptionStatus,
  updateAutobuyConfigApi,
  updateBillingAddressApi,
} from '../lib/api.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { formatCurrency, formatDate, formatNumber } from '../lib/format.js';
import { featuresFor, ladderDirection, planBlurbs, universalTerms } from '../lib/plan-copy.js';
import {
  billingCustomerQuery,
  billingInvoicesQuery,
  billingQuery,
  billingUpcomingQuery,
  magTokenTransactionsQuery,
  meQuery,
} from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// s120 — /settings/billing. Full Stripe-backed billing surface.
//
// Embedded sections (read paths via the api, render in our UI):
//   PlanSection             — current plan + status + tier picker
//   PaymentMethodSection    — card brand/last4, deep-link to Stripe to update
//   BillingAddressSection   — editable form, PATCH to Stripe customer.address
//   TaxIdsSection           — list + add/remove tax IDs
//   UpcomingInvoiceSection  — preview of the next charge
//   InvoiceHistorySection   — past invoices with PDF download links
//
// Card capture stays in Stripe's hosted surfaces (Billing Portal scoped
// to payment_method_update) — we deliberately don't run Stripe Elements
// here to avoid PCI scope creep.
//
// Deep-links handled here:
//   ?prompt=growth     → from the landing's Growth pricing card; that
//                        plan card is visually emphasised in the picker.
//   ?checkout=success  → Stripe Checkout completed; refresh state + toast.
//                        Webhook may not have processed yet — wording matches.
//   ?checkout=canceled → Stripe Checkout abandoned; toast tells the
//                        founder nothing changed.

interface BillingSearch {
  prompt?: PlanKey;
  checkout?: 'success' | 'canceled';
  /** [s264] Top-up Checkout redirect. Success → refresh balance +
   *  toast; canceled → mention no charge. */
  topup?: 'success' | 'canceled';
  /** Which pack the founder bought (only set with topup=success).
   *  Lets the toast read "1,000 tokens added" instead of generic. */
  pack?: 'small' | 'medium' | 'large';
}

export const Route = createFileRoute('/settings/billing')({
  validateSearch: (search: Record<string, unknown>): BillingSearch => {
    const out: BillingSearch = {};
    // Any rung can deep-link from the landing pricing cards. Validated
    // against PLAN_KEYS rather than a literal pair, which went stale the
    // moment the ladder grew.
    if (
      typeof search['prompt'] === 'string' &&
      (PLAN_KEYS as readonly string[]).includes(search['prompt'])
    ) {
      out.prompt = search['prompt'] as PlanKey;
    }
    if (search['checkout'] === 'success' || search['checkout'] === 'canceled') {
      out.checkout = search['checkout'] as 'success' | 'canceled';
    }
    if (search['topup'] === 'success' || search['topup'] === 'canceled') {
      out.topup = search['topup'] as 'success' | 'canceled';
    }
    if (search['pack'] === 'small' || search['pack'] === 'medium' || search['pack'] === 'large') {
      out.pack = search['pack'] as 'small' | 'medium' | 'large';
    }
    return out;
  },
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const billing = await queryClient.ensureQueryData(billingQuery());
    // Don't await the rest — they can lazy-load in the page. Pre-warm
    // with ensureQueryData so the cache has them, but if Stripe isn't
    // configured these all 503 and we don't want to block the page on
    // those failures.
    void queryClient.ensureQueryData(billingCustomerQuery()).catch(() => null);
    void queryClient.ensureQueryData(billingInvoicesQuery()).catch(() => []);
    void queryClient.ensureQueryData(billingUpcomingQuery()).catch(() => null);
    return { me, billing };
  },
  loader: ({ context }) => context as { me: unknown; billing: BillingState },
  component: BillingPage,
});

function BillingPage() {
  const { t } = useLingui();
  const { billing } = Route.useLoaderData();
  const search = Route.useSearch();
  const router = useRouter();
  const queryClient = useQueryClient();

  // Post-checkout side effects — toast + state refresh. Stripe redirects
  // back with ?checkout=success which fires here; the webhook may still
  // be in-flight, so we re-fetch billing state and trust eventual
  // consistency (Stripe is usually <1s).
  //
  // Ref guard prevents React 18 StrictMode's double-mount in dev from
  // firing two toasts before the URL navigation clears the search param.
  const checkoutHandledRef = useRef(false);
  // Snapshotted before the refetch below mutates the cache, so plan_changed
  // can report the move as a pair (from -> to) rather than just a landing spot.
  const planBefore = billing.plan;
  useEffect(() => {
    if (checkoutHandledRef.current) return;
    if (search.checkout === 'success') {
      checkoutHandledRef.current = true;
      magToast.success(
        t`thanks. mag will pick up where you left off.`,
        t`your plan should reflect the change in a moment.`,
      );
      void refreshQueries(queryClient, ['billing', 'billing-customer', 'billing-invoices']).then(
        () => {
          // Reported after the refetch, deliberately. `billing` in this scope
          // is the loader's pre-checkout read, so reporting from it would
          // attribute the upgrade to the rung the founder just LEFT. Stripe's
          // webhook is normally in before this resolves; if it somehow isn't,
          // the plan field degrades to the old value and the event still
          // records that a checkout completed.
          const fresh = queryClient.getQueryData<BillingState>(billingQuery().queryKey);
          const plan = fresh?.plan ?? null;
          trackConversion('plan_changed', {
            plan,
            plan_before: planBefore,
            value: plan ? PLANS[plan].basePriceUsd : null,
          });
          return router.invalidate();
        },
      );
      void router.navigate({ to: '/settings/billing', search: {} });
    } else if (search.checkout === 'canceled') {
      checkoutHandledRef.current = true;
      magToast.error(t`checkout canceled.`, t`no charges made; your plan is unchanged.`);
      void router.navigate({ to: '/settings/billing', search: {} });
    } else if (search.topup === 'success') {
      checkoutHandledRef.current = true;
      const pack = search.pack
        ? billing.magTokens.packs.find((p) => p.key === search.pack)
        : undefined;
      magToast.success(
        pack
          ? t`${formatNumber(pack.tokens)} mag tokens added.`
          : t`mag tokens added to your balance.`,
        t`mag is back online if she was waiting on tokens.`,
      );
      // The pack is already resolved above for the toast copy, so the money
      // and the token count come straight off it. Unlike plan_changed this
      // needs no refetch: a top-up pack's price is static, not a function of
      // whatever state the webhook left behind.
      trackConversion('tokens_purchased', {
        pack: pack?.key ?? search.pack ?? null,
        tokens: pack?.tokens ?? null,
        value: pack?.priceUsd ?? null,
      });
      void refreshQueries(queryClient, ['billing']).then(() => router.invalidate());
      void router.navigate({ to: '/settings/billing', search: {} });
    } else if (search.topup === 'canceled') {
      checkoutHandledRef.current = true;
      magToast.error(t`top-up canceled.`, t`no charges made; your balance is unchanged.`);
      void router.navigate({ to: '/settings/billing', search: {} });
    }
  }, [
    search.checkout,
    search.topup,
    search.pack,
    queryClient,
    router,
    billing.magTokens.packs,
    planBefore,
    t,
  ]);

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <WorkspaceNav active="billing" />
      <section className="mx-auto max-w-[820px] px-7 pb-16 pt-8">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>settings · billing</Trans>
        </p>
        <h1 className="mt-2 font-display text-[34px] font-black leading-[1.05] tracking-[-0.04em]">
          <Trans>your plan.</Trans>
        </h1>
        <p className="mt-3 max-w-[680px] text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
          <Trans>
            one number, no surprises. mag handles the work; stripe handles the bill. switch plans,
            update your card, or grab past receipts here.
          </Trans>
        </p>

        {!billing.stripeConfigured ? (
          <ConfigHintCard />
        ) : (
          <>
            <PlanSection billing={billing} preselected={search.prompt ?? null} />
            <SectionHeading>
              <Trans>mag tokens</Trans>
            </SectionHeading>
            <MagTokensSection magTokens={billing.magTokens} />
            <SectionHeading>
              <Trans>analytics usage</Trans>
            </SectionHeading>
            <AnalyticsUsageCard />
            <PaymentMethodSection />
            <BillingAddressSection />
            <TaxIdsSection />
            <UpcomingInvoiceSection />
            <InvoiceHistorySection />
          </>
        )}
      </section>
    </main>
  );
}

// ─── Plan + current subscription ───────────────────────────────────────

function PlanSection({
  billing,
  preselected,
}: {
  billing: BillingState;
  preselected: PlanKey | null;
}) {
  const { t } = useLingui();
  const [pendingPlan, setPendingPlan] = useState<PlanKey | null>(null);
  // Picker opens automatically when the visitor came in deep-linked from
  // the landing page (?prompt=growth) — they're already in "I want to
  // change my plan" frame. Otherwise it stays closed until they hit the
  // "change plan" button on the current-plan card.
  const [pickerOpen, setPickerOpen] = useState(preselected !== null);

  const handleSelectPlan = async (plan: PlanKey) => {
    if (plan === billing.plan && billing.subscriptionStatus === 'active') {
      magToast.error(
        t`you're already on ${PLANS[plan].label}.`,
        t`cancel from stripe billing if you want to leave the plan.`,
      );
      return;
    }
    setPendingPlan(plan);
    try {
      const { url } = await createCheckoutSessionApi(plan);
      window.location.href = url;
    } catch (err) {
      magToast.error("couldn't open checkout.", founderErrorCopy(err));
      setPendingPlan(null);
    }
  };

  return (
    <>
      <CurrentPlanCard billing={billing} onChangePlan={() => setPickerOpen(true)} />
      <SubscriptionBanner status={billing.subscriptionStatus} />
      <PlanPickerModal
        open={pickerOpen}
        plans={billing.plans}
        currentPlan={billing.plan}
        subscriptionStatus={billing.subscriptionStatus}
        preselected={preselected}
        pendingPlan={pendingPlan}
        onSelect={handleSelectPlan}
        onClose={() => setPickerOpen(false)}
      />
    </>
  );
}

function CurrentPlanCard({
  billing,
  onChangePlan,
}: {
  billing: BillingState;
  onChangePlan: () => void;
}) {
  const { t } = useLingui();
  const currentPlanDef = billing.plans.find((p) => p.key === billing.plan);
  const label = currentPlanDef?.label ?? billing.plan;
  const renewsLabel =
    billing.currentPeriodEnd && billing.subscriptionStatus !== 'canceled'
      ? t`renews ${formatDate(billing.currentPeriodEnd)}`
      : billing.subscriptionStatus === 'canceled' && billing.currentPeriodEnd
        ? t`access ends ${formatDate(billing.currentPeriodEnd)}`
        : t`no active subscription`;

  return (
    <article className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>current plan</Trans>
          </p>
          <p className="mt-1.5 font-display text-[24px] font-black leading-none tracking-[-0.02em] text-[var(--ink)]">
            {label}
          </p>
          <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            {renewsLabel}
          </p>
        </div>
        <button type="button" onClick={onChangePlan} className="btn btn-ghost text-[11px]">
          <Trans>change plan</Trans>
          <span className="btn-arrow">→</span>
        </button>
      </div>
      {currentPlanDef ? (
        // What's included on the current plan — surfaces the limits
        // (brands, events) without making the founder open the picker
        // to remember. Reads from the live BillingPlanDisplay so the
        // numbers stay in lock-step with PLANS in @magig/schemas.
        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-[var(--line)] pt-4 text-[12px]">
          <PlanFact label={t`brands`} value={String(currentPlanDef.maxBrands)} />
          <PlanFact
            label={t`analytics events / mo`}
            value={formatCount(currentPlanDef.monthlyAnalyticsEvents)}
          />
        </dl>
      ) : null}
    </article>
  );
}

function PlanFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {label}
      </dt>
      <dd className="font-display text-[15px] font-bold tracking-[-0.01em] text-[var(--ink)]">
        {value}
      </dd>
    </div>
  );
}

// ─── Plan picker modal ─────────────────────────────────────────────────

/** Per-plan long-form copy. Shown in the change-plan modal alongside
 *  the bullet list — the bullet list is "what you get", this paragraph
 *  is "who this is for + why this number". */
/** Format a count like 10_000 → "10,000" with thousand separators.
 *  Pulled into a helper so the same number formats identically
 *  everywhere it surfaces (plan card bullets, current-plan recap,
 *  future usage strip). */
function formatCount(n: number): string {
  return formatNumber(n);
}

type T = ReturnType<typeof useLingui>['t'];

// Module-level const until wave 4: a module-level t`` evaluates before
// any locale is active and bakes English in for the life of the
// process, so the table is built during render from the caller's `t`.

function PlanPickerModal({
  open,
  plans,
  currentPlan,
  subscriptionStatus,
  preselected,
  pendingPlan,
  onSelect,
  onClose,
}: {
  open: boolean;
  plans: BillingPlanDisplay[];
  currentPlan: PlanKey;
  subscriptionStatus: SubscriptionStatus;
  preselected: PlanKey | null;
  pendingPlan: PlanKey | null;
  onSelect: (key: PlanKey) => void;
  onClose: () => void;
}) {
  // Escape closes. Bind only while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pendingPlan === null) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, pendingPlan]);

  if (!open) return null;

  const node = (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop is intentionally non-interactive for assistive tech; Escape + the dialog's cancel button cover close.
    <div
      // Inline-style the fixed positioning to defeat any ancestor
      // transform/filter that would otherwise re-root `position: fixed`
      // to the wrong element. Backdrop opacity bumped to 60% so the
      // page underneath reads as clearly modal, not "still interactive".
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 60,
      }}
      className="flex items-start justify-center overflow-y-auto bg-[var(--ink)]/60 px-4 py-10 sm:px-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && pendingPlan === null) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-picker-title"
        // Inline width too — same defeat-the-ancestor reasoning. Single-
        // Wide enough for the four rungs side by side. It was 520px with a
        // single-column stack, which was right for TWO plans and became a
        // scroll-through-four-tall-cards once the ladder grew — you cannot
        // compare rungs you cannot see at once.
        //
        // 1080px is not invented: it is the landing page's own 4-up
        // breakpoint (globals.css:1366, .price-grid--four), and the grid
        // below falls back to 2x2 under it exactly as the landing does.
        // What made four columns readable there was lifting the repeated
        // terms out of the cards — see universalTerms() — not width alone.
        style={{ width: '100%', maxWidth: '1080px' }}
        className="overflow-hidden rounded-2xl border-[1.5px] border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--plum)]"
      >
        <div className="border-b border-[var(--line)] px-5 pb-4 pt-5">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>choose your plan</Trans>
          </p>
          <h2
            id="plan-picker-title"
            className="mt-1.5 font-display text-[22px] font-black leading-tight tracking-[-0.02em] text-[var(--ink)]"
          >
            <Trans>which mag do you need today?</Trans>
          </h2>
          <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/70">
            <Trans>
              same mag on every rung — what changes is how many brands she runs, how much she can do
              without asking, and how much work is included. switch any time.
            </Trans>
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan) => (
            <ExpandedPlanCard
              key={plan.key}
              plan={plan}
              currentPlan={currentPlan}
              subscriptionStatus={subscriptionStatus}
              allPlans={plans}
              preselected={preselected === plan.key}
              onSelect={onSelect}
              pending={pendingPlan === plan.key}
              anyPending={pendingPlan !== null}
            />
          ))}
        </div>
        {/* The terms true of every rung, said ONCE. They used to repeat
            verbatim inside all four cards — twelve identical lines, and the
            reason four cards could not sit side by side. Said once here they
            read as a promise about the product rather than a feature of a
            tier, which is also more honest: "cancel any time, no contract" is
            not something Growth gives you. Same move the landing page made
            (pricing.tsx, s529). */}
        <div className="border-t border-[var(--line)] px-5 py-3">
          <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[12px] text-[var(--ink)]/70">
            {universalTerms().map((term) => (
              <li key={term} className="flex items-center gap-1.5">
                <span aria-hidden className="text-[var(--spell)]">
                  {'•'}
                </span>
                {term}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-end border-t border-[var(--line)] bg-[var(--paper)] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={pendingPlan !== null}
            className="link-quiet text-[12px] disabled:opacity-60"
          >
            <Trans>close</Trans>
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

function ExpandedPlanCard({
  plan,
  currentPlan,
  subscriptionStatus,
  allPlans,
  preselected,
  onSelect,
  pending,
  anyPending,
}: {
  plan: BillingPlanDisplay;
  currentPlan: PlanKey;
  subscriptionStatus: SubscriptionStatus;
  allPlans: readonly BillingPlanDisplay[];
  preselected: boolean;
  onSelect: (key: PlanKey) => void;
  pending: boolean;
  anyPending: boolean;
}) {
  const { t } = useLingui();
  const isCurrent = plan.key === currentPlan && subscriptionStatus === 'active';
  // By price — a key comparison only works while there are exactly two
  // rungs, and there are four.
  const direction = ladderDirection(plan, currentPlan, allPlans);
  const isUpgrade = direction === 'upgrade';
  const isDowngrade = direction === 'downgrade';

  const emphasised = preselected || (isUpgrade && subscriptionStatus !== 'active');

  let cta: string;
  if (isCurrent) cta = t`current plan`;
  else if (isDowngrade) cta = t`downgrade`;
  else if (currentPlan === plan.key) cta = t`resubscribe`;
  else cta = t`choose plan`;

  const ctaDisabled = isCurrent || (anyPending && !pending);
  const blurb = planBlurbs()[plan.key];
  const features = featuresFor(plan);

  return (
    <article
      className={`flex flex-col rounded-2xl border bg-white p-5 ${
        emphasised
          ? 'border-[1.5px] border-[var(--ink)] shadow-[4px_4px_0_var(--plum)]'
          : 'border-[var(--line)]'
      }`}
    >
      <div className="flex items-baseline justify-between">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          ↘ {plan.label}
        </p>
        {isCurrent ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--spell)] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]">
            <Trans>current</Trans>
          </span>
        ) : null}
      </div>
      <p className="mt-2 font-display text-[32px] font-black leading-none tracking-[-0.02em] text-[var(--ink)]">
        ${plan.basePriceUsd}
        <span className="ml-1 font-mono text-[12px] font-normal text-[var(--ink)]/55">
          <Trans>/mo</Trans>
        </span>
      </p>
      <p className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {blurb.tagline}
      </p>
      <p className="mt-3 text-[13px] leading-[1.55] text-[var(--ink)]/75">{blurb.description}</p>
      <ul className="mt-4 flex flex-col gap-1.5 text-[13px] text-[var(--ink)]/85">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--spell)]" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {/* mt-auto, not mt-5: the grid stretches all four cards to equal
          height, but the bullet lists differ in length and wrapping, so a
          fixed top margin left the four CTAs at four different heights.
          Invisible while the cards were a single column; a ragged row of
          buttons the moment they sit side by side. */}
      <div className="mt-auto flex items-center justify-end pt-5">
        <button
          type="button"
          onClick={() => onSelect(plan.key)}
          disabled={ctaDisabled}
          className={`text-[11px] disabled:opacity-60 ${
            emphasised && !isCurrent ? 'btn' : 'btn btn-ghost'
          }`}
        >
          {pending ? t`opening…` : cta}
          {!isCurrent ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </article>
  );
}

function SubscriptionBanner({ status }: { status: SubscriptionStatus }) {
  if (status === 'past_due') {
    return (
      <article className="mt-4 rounded-2xl border-[1.5px] border-[var(--pulse)] bg-[var(--pulse)]/5 p-4">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--pulse)]">
          {'↘'} <Trans>payment past due</Trans>
        </p>
        <p className="mt-1.5 text-[13px] leading-[1.5] text-[var(--ink)]/85">
          <Trans>
            your last invoice didn't go through. update your payment method via{' '}
            <strong>manage billing</strong> — mag keeps working for now, but write actions pause if
            this stays past due.
          </Trans>
        </p>
      </article>
    );
  }
  if (status === 'canceled') {
    return (
      <article className="mt-4 rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-4">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/65">
          {'↘'} <Trans>subscription canceled</Trans>
        </p>
        <p className="mt-1.5 text-[13px] leading-[1.5] text-[var(--ink)]/85">
          <Trans>mag's still here — pick a plan below to start again any time.</Trans>
        </p>
      </article>
    );
  }
  return null;
}

// ─── Mag tokens — balance + top-up packs (s264) ───────────────────────
//
// Mag tokens are the billing unit (the mag-tokens doc (CommitMind)). One token =
// $0.05 of mag work. The subscription grants a monthly allotment; when
// it runs out mag's generative side pauses but scheduled work keeps
// shipping. Founders top up here to push past the included allotment.

function MagTokensSection({ magTokens }: { magTokens: BillingState['magTokens'] }) {
  const { t } = useLingui();
  const [pendingPack, setPendingPack] = useState<BillingTopupPackDisplay['key'] | null>(null);
  const handleBuyPack = async (pack: BillingTopupPackDisplay['key']) => {
    setPendingPack(pack);
    try {
      const { url } = await createTopupCheckoutSessionApi(pack);
      window.location.href = url;
    } catch (err) {
      magToast.error("couldn't open checkout.", founderErrorCopy(err));
      setPendingPack(null);
    }
  };

  const balance = magTokens.balance;
  const low = balance > 0 && balance < 200;
  const empty = balance <= 0;

  return (
    <>
      <article className="mt-6 rounded-2xl border border-[var(--line)] bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
              {'↘'} <Trans>current balance</Trans>
            </p>
            <p className="mt-1.5 font-display text-[28px] font-black leading-none tracking-[-0.02em] text-[var(--ink)]">
              {formatNumber(balance)}{' '}
              <span className="text-[16px] font-bold text-[var(--ink)]/55">
                <Trans>tokens</Trans>
              </span>
            </p>
            <p className="mt-1.5 text-[12.5px] leading-[1.5] text-[var(--ink)]/65">
              {empty
                ? t`mag's generative side is paused. scheduled drops still ship — top up to bring her back online.`
                : low
                  ? t`running low. top up before mag pauses on the next generative call.`
                  : t`mag bills against this balance every time she drafts, plans, or chats with you.`}
            </p>
          </div>
        </div>
      </article>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {magTokens.packs.map((pack) => (
          <TopupPackCard
            key={pack.key}
            pack={pack}
            pending={pendingPack === pack.key}
            disabled={pendingPack !== null && pendingPack !== pack.key}
            onBuy={() => handleBuyPack(pack.key)}
          />
        ))}
      </div>

      <TokenExplainerCard />
      <AutobuyCard autobuy={magTokens.autobuy} />
      <MagTokenLedger />
    </>
  );
}

// [s264] "what does a token buy" explainer — answers the founder's
// first question after seeing the chip + pack prices. Numbers are
// placeholders from the mag-tokens doc (CommitMind) §2 (rough back-of-envelope
// from Phase 0 cost data). Refine after another week of agent-name
// traffic + Phase 1.5 caching.
function TokenExplainerCard() {
  const { t } = useLingui();
  // [task cf7fef3] x5 of the old figures — a mag token is 1 cent of billed work
  // since c9032d2, not 5. These are the same operations at the same real cost;
  // only the unit moved. Missed in that change, which left the one surface
  // whose whole job is saying what a token is worth understating every row by
  // five.
  const items: Array<{ tokens: number; label: string }> = [
    { tokens: 5, label: t`one chat turn with mag` },
    { tokens: 10, label: t`one Instagram post drafted` },
    { tokens: 15, label: t`one takeover-read on an imported campaign` },
    { tokens: 30, label: t`one weekly brief` },
    { tokens: 75, label: t`one full competition refresh` },
  ];
  return (
    <article className="mt-3 rounded-2xl border border-dashed border-[var(--line)] bg-[var(--paper)] p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>what tokens buy</Trans>
      </p>
      <p className="mt-1.5 text-[12.5px] leading-[1.5] text-[var(--ink)]/70">
        <Trans>
          rough cost per operation. mag's reads + agent calls all decrement; you can see exactly
          what's burning in the activity panel below.
        </Trans>
      </p>
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-[12.5px] sm:grid-cols-2">
        {items.map(({ tokens, label }) => (
          <div key={label} className="flex items-baseline justify-between gap-3">
            <dt className="text-[var(--ink)]/85">{label}</dt>
            <dd className="font-mono tabular-nums text-[var(--ink)]/55">
              {'~'}
              {plural(tokens, { one: '# token', other: '# tokens' })}
            </dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

// [s264] Mag-token transactions panel — the last 30 rows of the
// append-only ledger so the founder can see what's burning their
// balance + when grants land. Renders a row per transaction with the
// kind, a short note, the delta (signed), and the resulting balance.
// Empty state = "nothing here yet" rather than a blank box.
function MagTokenLedger() {
  const { data: rows, isLoading } = useQuery(magTokenTransactionsQuery());

  if (isLoading) {
    return (
      <article className="mt-4 rounded-2xl border border-[var(--line)] bg-white p-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>recent activity</Trans>
        </p>
        <div className="mt-3 h-24 animate-pulse rounded-lg bg-[var(--line)]/30" />
      </article>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <article className="mt-4 rounded-2xl border border-[var(--line)] bg-white p-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>recent activity</Trans>
        </p>
        <p className="mt-3 text-[13px] text-[var(--ink)]/65">
          <Trans>
            no token activity yet. your first grant lands when your next subscription invoice
            processes.
          </Trans>
        </p>
      </article>
    );
  }

  return (
    <article className="mt-4 rounded-2xl border border-[var(--line)] bg-white p-5">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>recent activity</Trans>
      </p>
      <ul className="mt-3 divide-y divide-[var(--line)]">
        {rows.map((row) => (
          <LedgerRow key={row.id} row={row} />
        ))}
      </ul>
    </article>
  );
}

function LedgerRow({ row }: { row: MagTokenTransactionRow }) {
  const { t } = useLingui();
  const positive = row.deltaTokens > 0;
  const when = new Date(row.createdAt);
  const whenLabel = formatDate(when, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return (
    <li className="flex items-center justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-[13px] font-medium text-[var(--ink)]">
          {ledgerRowLabel(t, row)}
        </p>
        <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          {whenLabel}
        </p>
      </div>
      <div className="flex shrink-0 items-baseline gap-3">
        <span
          className={`font-mono text-[13px] tabular-nums ${
            positive ? 'text-[var(--plum)]' : 'text-[var(--ink)]/75'
          }`}
        >
          {positive ? '+' : ''}
          {formatNumber(row.deltaTokens)}
        </span>
        <span className="font-mono text-[11px] tabular-nums text-[var(--ink)]/45">
          → {formatNumber(row.balanceAfterTokens)}
        </span>
      </div>
    </li>
  );
}

/** Translate a row's kind + metadata into a one-line human label.
 *  Kept switch-style instead of a map so each branch can read its own
 *  metadata shape without a cast pyramid. */
function ledgerRowLabel(t: T, row: MagTokenTransactionRow): string {
  const m = row.metadata ?? {};
  switch (row.kind) {
    case 'subscription_grant': {
      if ((m as { backfill?: boolean }).backfill) return i18n._(msg`starter grant (backfill)`);
      // The plan key is a server identifier, not copy — it reads as-is
      // inside the parenthetical in every locale.
      const plan = (m as { plan?: string }).plan;
      return plan ? i18n._(msg`subscription renewal (${plan})`) : i18n._(msg`subscription renewal`);
    }
    case 'topup_purchase': {
      const tokens = (m as { packTokens?: number }).packTokens;
      return tokens
        ? i18n._(msg`top-up — ${formatNumber(tokens)} tokens`)
        : i18n._(msg`top-up purchase`);
    }
    case 'autobuy': {
      const tokens = (m as { packTokens?: number }).packTokens;
      return tokens
        ? i18n._(msg`auto-buy — ${formatNumber(tokens)} tokens`)
        : i18n._(msg`auto-buy`);
    }
    case 'llm_decrement': {
      // agentName is an internal agent identifier, left untranslated.
      const agent = (m as { agentName?: string }).agentName;
      return agent ? i18n._(msg`mag drafted (${agent})`) : i18n._(msg`mag worked`);
    }
    case 'admin_adjust':
      return i18n._(msg`manual adjustment`);
    case 'refund':
      return i18n._(msg`refund`);
    default:
      // Unknown ledger kind — the raw server enum, not copy.
      return row.kind;
  }
}

// [s264] Auto-buy controls. Off by default. When the founder turns it
// on the meter starts firing off-session charges every time the
// balance dips below the threshold. We charge the saved default
// payment method — if there's no card on file the charge errors and
// we surface that on the next render.
function AutobuyCard({ autobuy }: { autobuy: BillingState['magTokens']['autobuy'] }) {
  const { t } = useLingui();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(autobuy.enabled);
  // Form state — seeded from the persisted config so toggling open
  // shows what was last saved (or sensible defaults on first use).
  const [threshold, setThreshold] = useState<number>(autobuy.thresholdTokens ?? 100);
  const [pack, setPack] = useState<1000 | 5000 | 10000>(
    (autobuy.packTokens as 1000 | 5000 | 10000) ?? 1000,
  );
  const [saving, setSaving] = useState(false);

  const handleToggle = async (next: boolean) => {
    setOpen(next);
    if (!next && autobuy.enabled) {
      // Turning OFF — persist immediately, no extra form needed.
      setSaving(true);
      try {
        await updateAutobuyConfigApi({ enabled: false });
        await refreshQueries(queryClient, ['billing']);
        await router.invalidate();
        magToast.success(t`auto-buy off.`, t`i won't charge your card on low balance.`);
      } catch (err) {
        magToast.error("couldn't save.", founderErrorCopy(err));
        setOpen(true); // Revert toggle.
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAutobuyConfigApi({
        enabled: true,
        thresholdTokens: threshold,
        packTokens: pack,
      });
      await refreshQueries(queryClient, ['billing']);
      await router.invalidate();
      magToast.success(
        t`auto-buy on.`,
        t`i'll add ${formatNumber(pack)} tokens when you drop below ${formatNumber(threshold)}.`,
      );
    } catch (err) {
      magToast.error("couldn't save.", founderErrorCopy(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="mt-4 rounded-2xl border border-[var(--line)] bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>auto-buy</Trans>
          </p>
          <p className="mt-1.5 text-[13px] leading-[1.5] text-[var(--ink)]/85">
            {autobuy.enabled && autobuy.thresholdTokens != null && autobuy.packTokens != null
              ? t`on — i'll add ${formatNumber(autobuy.packTokens)} tokens when you drop below ${formatNumber(autobuy.thresholdTokens)}.`
              : t`off — your balance can hit zero and mag will pause until you top up manually.`}
          </p>
        </div>
        <label className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center">
          <input
            type="checkbox"
            className="peer sr-only"
            checked={open}
            disabled={saving}
            onChange={(e) => handleToggle(e.target.checked)}
          />
          <span className="absolute inset-0 rounded-full bg-[var(--line)] transition-colors peer-checked:bg-[var(--plum)] peer-disabled:opacity-50" />
          <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
        </label>
      </div>

      {open ? (
        <div className="mt-4 grid grid-cols-1 gap-4 border-t border-[var(--line)] pt-4 sm:grid-cols-2">
          <label className="block">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/65">
              <Trans>threshold (tokens)</Trans>
            </span>
            <input
              type="number"
              min={0}
              max={100_000}
              value={threshold}
              onChange={(e) => setThreshold(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
              className="mt-1.5 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-[13px] font-medium text-[var(--ink)] focus:border-[var(--plum)] focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/65">
              <Trans>pack to buy</Trans>
            </span>
            <select
              value={pack}
              onChange={(e) => setPack(Number(e.target.value) as 1000 | 5000 | 10000)}
              className="mt-1.5 w-full rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-[13px] font-medium text-[var(--ink)] focus:border-[var(--plum)] focus:outline-none"
            >
              {/* Prices are USD until wave 6 ships display currency; the
                  message is shaped so the amount can become a
                  placeholder without re-translating the line. */}
              <option value={1000}>{t`1,000 tokens — $49`}</option>
              <option value={5000}>{t`5,000 tokens — $229`}</option>
              <option value={10000}>{t`10,000 tokens — $429`}</option>
            </select>
          </label>
          <div className="flex justify-end sm:col-span-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary text-[11px]"
            >
              {saving ? t`saving…` : autobuy.enabled ? t`update` : t`turn on`}
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}

/** Pack sizes are server enum keys ('small' | 'medium' | 'large') that
 *  render as prose on the card, so they need a translated label rather
 *  than the raw key. */
function packSizeLabel(t: T, key: BillingTopupPackDisplay['key']): string {
  switch (key) {
    case 'small':
      return i18n._(msg`small pack`);
    case 'medium':
      return i18n._(msg`medium pack`);
    default:
      return i18n._(msg`large pack`);
  }
}

function TopupPackCard({
  pack,
  pending,
  disabled,
  onBuy,
}: {
  pack: BillingTopupPackDisplay;
  pending: boolean;
  disabled: boolean;
  onBuy: () => void;
}) {
  const { t } = useLingui();
  // Per-token unit cost surfaces the volume discount on medium/large
  // without making the founder do mental math.
  const perToken = pack.priceUsd / pack.tokens;
  return (
    <article className="flex flex-col rounded-2xl border border-[var(--line)] bg-white p-4 transition-colors hover:border-[var(--plum)]/40">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} {packSizeLabel(t, pack.key)}
      </p>
      <p className="mt-1.5 font-display text-[22px] font-black leading-none tracking-[-0.02em] text-[var(--ink)]">
        {formatNumber(pack.tokens)}{' '}
        <span className="text-[13px] font-bold text-[var(--ink)]/55">
          <Trans>tokens</Trans>
        </span>
      </p>
      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {t`$${perToken.toFixed(3)} / token`}
      </p>
      <div className="mt-auto flex items-end justify-between pt-4">
        <p className="font-display text-[18px] font-black tracking-[-0.02em] text-[var(--ink)]">
          ${pack.priceUsd}
        </p>
        <button
          type="button"
          onClick={onBuy}
          disabled={pending || disabled}
          className="btn btn-primary text-[11px]"
        >
          {pending ? t`opening…` : t`buy`}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </article>
  );
}

// ─── Payment method (read-only + deep-link to Stripe Portal) ──────────

function PaymentMethodSection() {
  const { t } = useLingui();
  // Shared customer data via React Query — all sections read from the
  // same cache, so we fetch once and re-render together.
  const { data: customer, isLoading } = useQuery(billingCustomerQuery());
  const [opening, setOpening] = useState(false);

  const handleOpenPortal = async () => {
    setOpening(true);
    try {
      const { url } = await createPaymentMethodPortalApi();
      window.location.href = url;
    } catch (err) {
      magToast.error("couldn't open card update.", founderErrorCopy(err));
      setOpening(false);
    }
  };

  const pm = customer?.defaultPaymentMethod ?? null;
  const ctaLabel = opening ? t`opening…` : pm ? t`update card` : t`add card`;

  return (
    <>
      <SectionHeading>
        <Trans>payment method</Trans>
      </SectionHeading>
      <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
        {isLoading ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>loading…</Trans>
          </p>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            {pm ? (
              <div className="flex items-center gap-3">
                <BrandLogo brand={pm.brand} />
                <div>
                  <p className="text-[14px] font-bold tracking-[-0.01em] text-[var(--ink)]">
                    {capitalize(pm.brand ?? 'card')} ···· {pm.last4 ?? '????'}
                  </p>
                  <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                    {pm.expMonth != null && pm.expYear != null
                      ? t`expires ${String(pm.expMonth).padStart(2, '0')}/${String(pm.expYear).slice(-2)}`
                      : ''}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-[13px] leading-[1.5] text-[var(--ink)]/70">
                <Trans>no card on file. add one to keep mag running past your current cycle.</Trans>
              </p>
            )}
            {/* Always-visible action button — same Stripe-hosted card-
                update flow whether we're adding a first card or
                replacing one. Stripe owns the form (PCI scope stays
                at zero on our side). */}
            <button
              type="button"
              onClick={handleOpenPortal}
              disabled={opening}
              className="btn btn-ghost text-[11px] disabled:opacity-60"
            >
              {ctaLabel}
              <span className="btn-arrow">→</span>
            </button>
          </div>
        )}
      </article>
    </>
  );
}

function BrandLogo({ brand }: { brand: string | null }) {
  // Simple text badge — keeps the page free of brand-logo SVG bundles
  // we don't otherwise ship. The font + spacing read like a small
  // type-set card brand.
  const label = (brand ?? 'card').toUpperCase().slice(0, 5);
  return (
    <span className="inline-flex h-8 min-w-[44px] items-center justify-center rounded-md border border-[var(--line)] bg-[var(--paper)] px-2 font-mono text-[10px] font-bold tracking-[0.08em] text-[var(--ink)]/85">
      {label}
    </span>
  );
}

// ─── Billing address ───────────────────────────────────────────────────

function BillingAddressSection() {
  const queryClient = useQueryClient();
  const { data: customer, isLoading } = useQuery(billingCustomerQuery());
  const [editing, setEditing] = useState(false);

  return (
    <>
      <SectionHeading>
        <Trans>billing address</Trans>
      </SectionHeading>
      <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
        {isLoading ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>loading…</Trans>
          </p>
        ) : !customer ? (
          <p className="text-[13px] leading-[1.5] text-[var(--ink)]/70">
            <Trans>
              no billing address yet. stripe collects one when you go through checkout — or you can
              add it manually here once you have an active subscription.
            </Trans>
          </p>
        ) : editing ? (
          <AddressForm
            initial={customer.address}
            onCancel={() => setEditing(false)}
            onSaved={(addr) => {
              // Mutate the cached customer in place so any other section
              // reading from billingCustomerQuery sees the new address
              // immediately. React Query merges the new value into the
              // cache; subsequent reads return the patched record.
              queryClient.setQueryData(['billing', 'customer'], {
                ...customer,
                address: addr,
              });
              setEditing(false);
            }}
          />
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="text-[13.5px] leading-[1.55] text-[var(--ink)]/85">
              <AddressDisplay address={customer.address} />
            </div>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="link-quiet text-[12px]"
            >
              {'↘'} <Trans>edit</Trans>
            </button>
          </div>
        )}
      </article>
    </>
  );
}

function AddressDisplay({ address }: { address: BillingAddress }) {
  const hasAny = Object.values(address).some((v) => v && v.trim() !== '');
  if (!hasAny) {
    return (
      <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        <Trans>no address on file</Trans>
      </p>
    );
  }
  return (
    <address className="not-italic">
      {address.line1 ? <div>{address.line1}</div> : null}
      {address.line2 ? <div>{address.line2}</div> : null}
      <div>{[address.city, address.state, address.postalCode].filter(Boolean).join(', ')}</div>
      {address.country ? <div className="uppercase">{address.country}</div> : null}
    </address>
  );
}

function AddressForm({
  initial,
  onCancel,
  onSaved,
}: {
  initial: BillingAddress;
  onCancel: () => void;
  onSaved: (next: BillingAddress) => void;
}) {
  const { t } = useLingui();
  const [line1, setLine1] = useState(initial.line1 ?? '');
  const [line2, setLine2] = useState(initial.line2 ?? '');
  const [city, setCity] = useState(initial.city ?? '');
  const [state, setState] = useState(initial.state ?? '');
  const [postalCode, setPostalCode] = useState(initial.postalCode ?? '');
  const [country, setCountry] = useState(initial.country ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmedCountry = country.trim().toUpperCase();
    if (trimmedCountry && trimmedCountry.length !== 2) {
      magToast.error(t`country must be a 2-letter code.`, t`e.g. US, GB, MX, BR.`);
      return;
    }
    setSaving(true);
    try {
      // Send all fields explicitly so empty inputs clear the value.
      const next = await updateBillingAddressApi({
        line1: line1.trim() || null,
        line2: line2.trim() || null,
        city: city.trim() || null,
        state: state.trim() || null,
        postalCode: postalCode.trim() || null,
        country: trimmedCountry || null,
      });
      magToast.success(t`billing address saved.`);
      onSaved(next);
    } catch (err) {
      magToast.error("couldn't save address.", founderErrorCopy(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
      className="flex flex-col gap-3"
    >
      <label className="field">
        <span className="field-label">
          <Trans>street</Trans>
        </span>
        <input
          type="text"
          value={line1}
          onChange={(e) => setLine1(e.target.value)}
          placeholder={t`123 main st`}
          disabled={saving}
          maxLength={200}
        />
      </label>
      <label className="field">
        <span className="field-label">
          <Trans>apt / suite (optional)</Trans>
        </span>
        <input
          type="text"
          value={line2}
          onChange={(e) => setLine2(e.target.value)}
          placeholder={t`apt 4b`}
          disabled={saving}
          maxLength={200}
        />
      </label>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="field">
          <span className="field-label">
            <Trans>city</Trans>
          </span>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={saving}
            maxLength={120}
          />
        </label>
        <label className="field">
          <span className="field-label">
            <Trans>state / region</Trans>
          </span>
          <input
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value)}
            disabled={saving}
            maxLength={120}
          />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="field">
          <span className="field-label">
            <Trans>postal code</Trans>
          </span>
          <input
            type="text"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            disabled={saving}
            maxLength={40}
          />
        </label>
        <label className="field">
          <span className="field-label">
            <Trans>country (2-letter)</Trans>
          </span>
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase())}
            placeholder={t`US`}
            disabled={saving}
            maxLength={2}
            style={{ textTransform: 'uppercase' }}
          />
        </label>
      </div>
      <div className="mt-1 flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="link-quiet text-[12px] disabled:opacity-60"
        >
          <Trans>cancel</Trans>
        </button>
        <button type="submit" disabled={saving} className="btn disabled:opacity-60">
          {saving ? t`saving…` : t`save address`}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </form>
  );
}

// ─── Tax IDs ───────────────────────────────────────────────────────────

// Most common tax-id types per Stripe. Not exhaustive (Stripe supports
// dozens) — but covers the major founder geographies. "Other" lets the
// founder type a Stripe type-string directly for the long tail.
//
// The `value`s are Stripe type codes and the labels are the tax
// instruments' own names ("EU VAT", "BR CNPJ") — neither is copy, and
// both read the same in every locale. Only the "other" row is prose,
// so this is a function of `t` rather than a module-level const.
function taxIdTypes(t: T): Array<{ value: string; label: string }> {
  return [
    { value: 'us_ein', label: 'US EIN' },
    { value: 'eu_vat', label: 'EU VAT' },
    { value: 'gb_vat', label: 'UK VAT' },
    { value: 'ca_gst_hst', label: 'CA GST/HST' },
    { value: 'au_abn', label: 'AU ABN' },
    { value: 'br_cnpj', label: 'BR CNPJ' },
    { value: 'mx_rfc', label: 'MX RFC' },
    { value: 'in_gst', label: 'IN GST' },
    { value: 'other', label: i18n._(msg`other (type Stripe code)`) },
  ];
}

function TaxIdsSection() {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { data: customer, isLoading } = useQuery(billingCustomerQuery());
  const [adding, setAdding] = useState(false);

  const handleAdd = async (taxId: BillingTaxId) => {
    if (!customer) return;
    queryClient.setQueryData(['billing', 'customer'], {
      ...customer,
      taxIds: [...customer.taxIds, taxId],
    });
    setAdding(false);
  };

  const handleRemove = async (id: string) => {
    if (!customer) return;
    try {
      await removeBillingTaxIdApi(id);
      queryClient.setQueryData(['billing', 'customer'], {
        ...customer,
        taxIds: customer.taxIds.filter((t) => t.id !== id),
      });
      magToast.success(t`tax id removed.`);
    } catch (err) {
      magToast.error("couldn't remove tax id.", founderErrorCopy(err));
    }
  };

  return (
    <>
      <SectionHeading>
        <Trans>tax ids</Trans>
      </SectionHeading>
      <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
        {isLoading ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>loading…</Trans>
          </p>
        ) : !customer ? (
          <p className="text-[13px] leading-[1.5] text-[var(--ink)]/70">
            <Trans>
              no stripe customer yet. add a tax id once you have an active subscription — it'll show
              up on every invoice.
            </Trans>
          </p>
        ) : (
          <>
            {customer.taxIds.length === 0 ? (
              <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                <Trans>no tax ids on file</Trans>
              </p>
            ) : (
              <ul className="flex flex-col">
                {customer.taxIds.map((t) => (
                  <li
                    key={t.id}
                    className="flex items-baseline justify-between gap-3 border-b border-[var(--line)] py-2 last:border-b-0"
                  >
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]/70">
                        {t.type}
                      </span>
                      <span className="font-mono text-[12.5px] text-[var(--ink)]/90">
                        {t.value}
                      </span>
                      {t.verification?.status && t.verification.status !== 'verified' ? (
                        <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                          · {t.verification.status}
                        </span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemove(t.id)}
                      className="link-quiet text-[11px] text-[var(--pulse)]"
                    >
                      <Trans>remove</Trans>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex items-center justify-end">
              {adding ? null : (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="link-quiet text-[12px]"
                >
                  {'↘'} <Trans>add tax id</Trans>
                </button>
              )}
            </div>
            {adding ? <TaxIdForm onCancel={() => setAdding(false)} onAdded={handleAdd} /> : null}
          </>
        )}
      </article>
    </>
  );
}

function TaxIdForm({
  onCancel,
  onAdded,
}: {
  onCancel: () => void;
  onAdded: (taxId: BillingTaxId) => void;
}) {
  const { t } = useLingui();
  const [type, setType] = useState<string>('us_ein');
  const [customType, setCustomType] = useState('');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const effectiveType = type === 'other' ? customType.trim() : type;
    if (!effectiveType || !value.trim()) {
      magToast.error(t`tax id needs a type and a value.`);
      return;
    }
    setSaving(true);
    try {
      const created = await addBillingTaxIdApi({ type: effectiveType, value: value.trim() });
      magToast.success(t`tax id added.`);
      onAdded(created);
    } catch (err) {
      magToast.error("couldn't add tax id.", founderErrorCopy(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
      // No paper-tinted inner box — matches the address form's bare
      // layout directly on the section card.
      className="mt-4 flex flex-col gap-3"
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: `<Select>` is a custom control whose internal button carries the ariaLabel — biome can't see through the component boundary. */}
        <label className="field">
          <span className="field-label">
            <Trans>type</Trans>
          </span>
          <Select
            value={type}
            onChange={(next) => setType(next)}
            options={taxIdTypes(t).map((row) => ({ value: row.value, label: row.label }))}
            disabled={saving}
            ariaLabel={t`tax id type`}
          />
        </label>
        {type === 'other' ? (
          <label className="field">
            <span className="field-label">
              <Trans>stripe type code</Trans>
            </span>
            <input
              type="text"
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              placeholder={t`e.g. ar_cuit`}
              disabled={saving}
              maxLength={40}
            />
          </label>
        ) : null}
      </div>
      <label className="field">
        <span className="field-label">
          <Trans>value</Trans>
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="123456789"
          disabled={saving}
          maxLength={80}
        />
      </label>
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="link-quiet text-[12px] disabled:opacity-60"
        >
          <Trans>cancel</Trans>
        </button>
        <button type="submit" disabled={saving} className="btn disabled:opacity-60">
          {saving ? t`adding…` : t`add tax id`}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </form>
  );
}

// ─── Upcoming invoice ──────────────────────────────────────────────────

function UpcomingInvoiceSection() {
  const { data: upcoming, isLoading } = useQuery(billingUpcomingQuery());

  // No active subscription → upcoming is null → hide the section
  // entirely (no empty state, no loading state — silent).
  if (!isLoading && upcoming === null) return null;

  return (
    <>
      <SectionHeading>
        <Trans>upcoming</Trans>
      </SectionHeading>
      <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
        {isLoading || !upcoming ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>loading…</Trans>
          </p>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-display text-[26px] font-black leading-none tracking-[-0.02em] text-[var(--ink)]">
                {formatMoney(upcoming.total, upcoming.currency)}
              </p>
              <p className="mt-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                <Trans>
                  period · {formatUnixDate(upcoming.periodStart)} →{' '}
                  {formatUnixDate(upcoming.periodEnd)}
                </Trans>
              </p>
            </div>
            <p className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
              {plural(upcoming.lineItems.length, { one: '# line item', other: '# line items' })}
            </p>
          </div>
        )}
      </article>
    </>
  );
}

// ─── Invoice history ───────────────────────────────────────────────────

function InvoiceHistorySection() {
  const { data: invoices, isLoading } = useQuery(billingInvoicesQuery());

  return (
    <>
      <SectionHeading>
        <Trans>invoice history</Trans>
      </SectionHeading>
      <article className="rounded-2xl border border-[var(--line)] bg-white p-5">
        {isLoading || !invoices ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <Trans>loading…</Trans>
          </p>
        ) : invoices.length === 0 ? (
          <p className="text-[13px] leading-[1.5] text-[var(--ink)]/70">
            <Trans>
              no invoices yet. your first one shows up here a few minutes after checkout.
            </Trans>
          </p>
        ) : (
          <ul className="flex flex-col">
            {invoices.map((inv) => (
              <li
                key={inv.id}
                className="flex items-baseline justify-between gap-3 border-b border-[var(--line)] py-2.5 last:border-b-0"
              >
                <div className="flex min-w-0 items-baseline gap-3">
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                    {formatUnixDate(inv.created)}
                  </span>
                  <span className="font-mono text-[12px] text-[var(--ink)]/85">
                    {inv.number ?? inv.id}
                  </span>
                </div>
                <div className="flex items-baseline gap-3">
                  <StatusBadge status={inv.status} />
                  <span className="font-display text-[14px] font-bold text-[var(--ink)]">
                    {formatMoney(inv.total, inv.currency)}
                  </span>
                  {inv.invoicePdf ? (
                    <a
                      href={inv.invoicePdf}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="link-quiet text-[11px]"
                    >
                      <Trans>pdf</Trans> →
                    </a>
                  ) : inv.hostedInvoiceUrl ? (
                    <a
                      href={inv.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="link-quiet text-[11px]"
                    >
                      <Trans>view</Trans> →
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </article>
    </>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const tone =
    status === 'paid'
      ? 'bg-[var(--spell)] text-[var(--ink)]'
      : status === 'open' || status === 'draft'
        ? 'border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]/70'
        : status === 'uncollectible' || status === 'void'
          ? 'bg-[var(--pulse)] text-[var(--cream)]'
          : 'border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]/70';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.08em] ${tone}`}
    >
      {status}
    </span>
  );
}

// ─── Shared helpers ────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-8 mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
      ↘ {children}
    </p>
  );
}

function ConfigHintCard() {
  return (
    <div className="mt-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--plum)]">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
        {'↘'} <Trans>stripe isn't configured</Trans>
      </p>
      <p className="mt-2 mag-voice text-[18px] leading-tight text-[var(--ink)]">
        <Trans>
          "billing's stubbed out here. set the three stripe envs in{' '}
          <code className="font-mono text-[14px]">apps/api/.env.local</code> and restart the api."
        </Trans>
      </p>
      {/* Env var names and the doc title are identifiers, not copy —
          held in expression containers so they read as data. */}
      <ul className="mt-4 flex flex-col gap-1 font-mono text-[12px] text-[var(--ink)]/85">
        <li>{'STRIPE_SECRET_KEY=sk_test_…'}</li>
        <li>{'STRIPE_WEBHOOK_SECRET=whsec_…'}</li>
        <li>{'STRIPE_BILLING_RETURN_URL=http://localhost:5173/settings/billing'}</li>
      </ul>
      <p className="mt-4 text-[12.5px] leading-[1.5] text-[var(--ink)]/65">
        <Trans>full walk-through in</Trans>{' '}
        <code className="font-mono">{'the Stripe stage-2 billing doc (CommitMind)'}</code>.
      </p>
    </div>
  );
}

function formatMoney(amount: number, currency: string): string {
  // Stripe returns the smallest currency unit (cents for USD). Most
  // currencies have 2 decimal places; zero-decimal (JPY, KRW) and
  // three-decimal (BHD) currencies exist but Magig prices in USD, so
  // we assume /100 unless we ship to a zero-decimal market.
  // Already took the currency from Stripe — only the LOCALE was hardcoded,
  // so a Spanish founder saw "$1,234.50" where the convention is
  // "1234,50 US$". formatCurrency keeps the currency and fixes the locale.
  return formatCurrency(amount / 100, currency.toUpperCase());
}

function formatUnixDate(unixSeconds: number): string {
  return formatDate(unixSeconds * 1000);
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
